/**
 * shell/ResearchShared.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Chart components + series-building helpers shared across the three
 * research pages (ResearchDashboard's squares grid, ResearchVariablePage,
 * ResearchChildDashboard). Pulled out of what used to be one combined
 * ResearchDashboard.jsx so the three pages can't quietly drift apart into
 * three slightly-different copies of the same SVG line chart.
 */

import { PANCHAKOSHA, KOSHA_LABEL_BY_ID, KOSHA_COLOR_BY_ID } from "@/constants/ncfFramework";

export const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

export const KOSHA_LABELS = KOSHA_LABEL_BY_ID;
export const KOSHA_COLORS = KOSHA_COLOR_BY_ID;

// Deterministic category → color mapping so the legend is stable across
// reloads and filter changes (never randomly assigned).
export const CATEGORY_COLORS = {
  'Hearing Impairment':         '#2563EB',
  'Autism Spectrum Disorder':   '#9333EA',
  'Down Syndrome':              '#16A34A',
  'Cerebral Palsy':             '#EA580C',
  'Intellectual Disability':    '#0891B2',
  'Visual Impairment':          '#B45309',
  'Speech & Language Disorder': '#DC2626',
  'Global Developmental Delay': '#7C3AED',
  'Multiple Disabilities':      '#475569',
  'Other':                      '#9CA3AF',
  'Unclassified':               '#D1D5DB',
};

export const CARD_PALETTE = ['#2563EB', '#16A34A', '#EA580C', '#9333EA', '#0891B2', '#DC2626', '#B45309', '#475569'];
export const selectStyle = { padding: '7px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 12, background: '#fff', color: '#374151' };

// ── Generic small-multiples line chart ──────────────────────────────────────
// series: [{ key, color, points: [{x,y}], opacity? }]. No library — hand-
// rolled SVG, matching the RadarChart/Sparkline convention already used in
// ProgressTracker.jsx rather than introducing a new chart dependency.
export function MultiLineChart({ series, width = 760, height = 160, yDomain, yFormat = v => Math.round(v * 100) / 100, thin = false, showAxisLabels = true }) {
  const allPoints = series.flatMap(s => s.points || []);
  if (!allPoints.length) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: 12 }}>No data yet</div>;
  }
  const xs = allPoints.map(p => p.x);
  const ys = allPoints.map(p => p.y);
  const xMin = Math.min(...xs, 0), xMax = Math.max(...xs, 1);
  const [yMin, yMax] = yDomain || [Math.min(...ys), Math.max(...ys) || 1];
  const padL = showAxisLabels ? 38 : 4, padR = 12, padT = 10, padB = showAxisLabels ? 20 : 4;
  const innerW = width - padL - padR, innerH = height - padT - padB;
  const sx = x => padL + ((x - xMin) / (xMax - xMin || 1)) * innerW;
  const sy = y => padT + innerH - ((y - yMin) / (yMax - yMin || 1)) * innerH;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      {showAxisLabels && [0, 0.5, 1].map(f => {
        const y = yMin + f * (yMax - yMin);
        return (
          <g key={f}>
            <line x1={padL} y1={sy(y)} x2={width - padR} y2={sy(y)} stroke="#EEF1F6" strokeWidth={1} />
            <text x={padL - 6} y={sy(y)} textAnchor="end" dominantBaseline="middle" fontSize={9} fill="#9CA3AF">{yFormat(y)}</text>
          </g>
        );
      })}
      {series.map((s, i) => {
        const pts = [...s.points].sort((a, b) => a.x - b.x);
        const d = pts.map((p, j) => `${j === 0 ? 'M' : 'L'} ${sx(p.x)} ${sy(p.y)}`).join(' ');
        return (
          <path key={s.key || i} d={d} fill="none" stroke={s.color}
            strokeWidth={thin ? 1 : 1.8} strokeOpacity={s.opacity ?? (thin ? 0.45 : 1)}
            strokeLinejoin="round" strokeLinecap="round" />
        );
      })}
      {showAxisLabels && (
        <>
          <text x={padL} y={height - 4} fontSize={9} fill="#9CA3AF">{Math.round(xMin)}d</text>
          <text x={width - padR} y={height - 4} fontSize={9} fill="#9CA3AF" textAnchor="end">{Math.round(xMax)}d since induction</text>
        </>
      )}
    </svg>
  );
}

export function Track({ title, unit, series, yDomain, yFormat, thin }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #E5E7EB', padding: '14px 16px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: '#111827' }}>{title}</div>
        <div style={{ fontSize: 11, color: '#9CA3AF' }}>{unit}</div>
      </div>
      <MultiLineChart series={series} yDomain={yDomain} yFormat={yFormat} thin={thin} />
      {series.length > 1 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          {series.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color }} />
              <span style={{ fontSize: 10, color: '#6B7280' }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Per-child helpers — build Track series from one /children/:code payload ─
export function koshaSeriesFrom(trend, scoreField, transform = v => v) {
  return PANCHAKOSHA.map(k => ({
    key: k, label: KOSHA_LABELS[k], color: KOSHA_COLORS[k],
    points: trend.map(t => ({ x: t.daysSinceInduction, y: transform(t[scoreField]?.[k]) })).filter(p => p.y != null),
  })).filter(s => s.points.length);
}
export function cardSeriesFrom(cardLevelTrend, field) {
  const cardIds = [...new Set(cardLevelTrend.map(r => r.cardId))];
  return cardIds.map((id, i) => ({
    key: id, label: id, color: CARD_PALETTE[i % CARD_PALETTE.length],
    points: cardLevelTrend.filter(r => r.cardId === id && r[field] != null)
      .map(r => ({ x: r.daysSinceInduction, y: r[field] })),
  })).filter(s => s.points.length);
}
export function moduleSeriesFrom(trend) {
  const types  = [...new Set(trend.map(t => t.moduleType))];
  const colors = { worksheet: '#2563EB', activitycard: '#9333EA', storybook: '#0891B2', flashcard: '#EA580C' };
  return types.map(mt => ({
    key: mt, label: mt, color: colors[mt] || '#6B7280',
    points: trend.filter(t => t.moduleType === mt && t.accuracy != null).map(t => ({ x: t.daysSinceInduction, y: t.accuracy })),
  })).filter(s => s.points.length);
}

// ── Across-group helper — turn a /group/:variable response into chart series
// One line per child, fanned across every disability category it belongs
// to, grouped by category for the legend counts. Never averaged.
export function groupSeriesFrom(series) {
  const lines = series.map(s => ({
    key: `${s.researchCode}-${s.category}`,
    color: CATEGORY_COLORS[s.category] || '#9CA3AF',
    points: s.points.map(p => ({ x: p.daysSinceInduction, y: p.value })),
  })).filter(l => l.points.length);

  const byCategory = {};
  for (const s of series) {
    if (!s.points.length) continue;
    (byCategory[s.category] ||= []).push(s.researchCode);
  }
  return { lines, byCategory };
}